// ChatMessage — one transcript entry, rendered by kind AND density, pure.
//
// .claude/research/design/02-03: the transcript is the HISTORY (distinct from the composer
// intake and the in-document delivery). A message is a discriminated union — an
// author message (the human's plain words), an agent message (markdown, the
// agent's concise + warm voice via Prose), or a PROPOSAL card (the pointer at an
// in-document change). This component is the per-kind render: a match over the
// `ChatEntry` discriminant, so a new entry kind breaks compilation here until it
// is handled.
//
// DENSITY (chat-ux.md §3 — the anchor fix): `full` is the roomy reading contract
// every turn a full artifact); `compact` is the quick-ask contract, a DIFFERENT
// reading contract, not a shrunk one. At compact the atoms themselves change:
//   • author → one dim line (context for the answer, not a co-equal artifact);
//   • agent  → prose CLAMPED to a few lines + a "more" expander;
//   • proposal → a ONE-LINE chip pointing at the change, not the tall card.
// REQ-UI-004 ("long AI content must not flood the transcript" / "summarize, don't
// flood") governs the AMBIENT surfaces — at `compact` the agent body clamps to a
// few lines behind a "more"/"less" expander. CARVE-OUT (owner decision,
// 2026-08-31): at `full` density the conversation answer is the primary artifact
// the author came to read, so it renders UNCLIPPED — no clamp, no expander.
// Summary-first still rightly governs compact/ambient surfaces; it does not
// govern the roomy reading tab.
// Text always stays in the DOM (the compact clamp is CSS-visual only) so
// assistive tech reads the whole message regardless of the visual cap.
//
// The author/agent split is a genuine structural distinction (two authors, one
// algebra — .claude/research/design/03), not a flag: the human's words are plain text
// (pre-wrapped, never parsed as markdown — their asterisks are literal), the
// agent's are markdown rendered through Prose.
//
// Composes Prose (agent body), ProposalCard / ProposalSummaryText (proposal),
// Text (author body). Pure/props-driven: `entry` + `density` pick the render;
// the proposal pointer intents report UP for the proposal kind only. The clamp's
// expand/collapse is view-local UI state (like the transcript's scroll pinning),
// not app state.

import * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
    type ReactNode,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { formatRecency } from "../../../app/recency.js";
import { useLiveElapsed } from "../../../hooks/useLiveElapsed.js";
import { useNow } from "../../../hooks/useNow.js";
import type { ComposerResourceRef } from "../../Composer/PowerComposer/types.js";
import { Icon } from "../../Icon/Icon.js";
import { IconButton } from "../../IconButton/IconButton.js";
import type { FocusTarget, ResourceRef } from "../../Navigation/types.js";
import { Prose, type RenderProseLink } from "../../Prose/Prose.js";
import { Text } from "../../Text/Text.js";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import { ActionOffer } from "../ActionOffer/ActionOffer.js";
import { CitationMarkers, SourcesList } from "../Citations/Citations.js";
import { InformationRequestCard } from "../InformationRequestCard/InformationRequestCard.js";
import { InformationRequestPendingCard } from "../InformationRequestCard/InformationRequestPendingCard.js";
import {
    InlineLink,
    type ResolveInlineLink,
    selectInlineLinkView,
} from "../InlineLink/InlineLink.js";
import { MaterialCard } from "../MaterialCard/MaterialCard.js";
import { MessageAttachmentIndicator } from "../MessageAttachmentIndicator/MessageAttachmentIndicator.js";
import { ObjectLine } from "../ObjectLine/ObjectLine.js";
import { PlanPreview } from "../PlanPreview/PlanPreview.js";
import {
    ProposalCard,
    ProposalSummaryText,
} from "../ProposalCard/ProposalCard.js";
import type { ChatDensity } from "../presentation.js";
import {
    AnsweredQuestions,
    SkippedQuestions,
} from "../QuestionSheet/QuestionTrail.js";
import { StreamingProse } from "../StreamingProse/StreamingProse.js";
import { ThreadNotice } from "../ThreadNotice/ThreadNotice.js";
import type { NoticeContext } from "../ThreadNotice/threadNoticeCopy.js";
import { AgentActivityTrail } from "../ToolActivityView/AgentActivityTrail.js";
import { type TrailGlyph, TrailLine } from "../TrailLine/TrailLine.js";
import type {
    ChatEntry,
    ChatFieldAnswer,
    FormSourceOption,
    InterruptionAction,
    MaterialBuildingEntry,
    MaterialKind,
    ProposalEntry,
    ProposalOpCount,
} from "../types.js";
import { AuthorBubble, type UnsentState } from "./AuthorBubble.js";
import "./ChatMessage.css";

// The per-message relative time, ticking. `then` is epoch ms
// (ChatEntry.timestamp). ONE formatter app-wide (G14): the same compact
// `recency` labels the rail shows ("now" / "1m" / "3h" / "12 aug"), so the
// transcript and the rail never disagree on wording. A real <time> element
// carries the machine-readable instant and the absolute time on hover.
const MessageTimestamp = ({
    timestamp,
}: {
    readonly timestamp: number;
}): ReactNode => {
    const { i18n } = useLingui();
    const now = useNow(30_000);
    const iso = new Date(timestamp).toISOString();
    const recency = formatRecency(iso, now, i18n.locale);
    return recency === undefined ? null : (
        <time
            className="bilda-chat-message__timestamp"
            dateTime={iso}
            title={recency.absolute}
        >
            {recency.relative}
        </time>
    );
};

// The live BUILDING material card (F114). A thin SHELL wrapper: the pure
// MaterialCard takes elapsed as a plain number, but the build's stamped elapsed
// only advances when a server delta arrives — frozen between deltas. This
// wrapper reads the ticking `useNow` seam (via useLiveElapsed) so the m:ss timer
// advances every second, then hands the pure card a live `elapsedMs`. The card
// itself stays a pure projection.
const BuildingMaterialCard = ({
    entry,
    onStop,
    onOpenPane,
}: {
    readonly entry: MaterialBuildingEntry;
    readonly onStop?: () => void;
    readonly onOpenPane?: () => void;
}): ReactNode => {
    const running =
        entry.build.status === "planning" || entry.build.status === "building";
    const elapsedMs = useLiveElapsed(entry.elapsedMs, running);
    return (
        <MaterialCard
            state={{
                type: "building",
                build: entry.build,
                elapsedMs,
                onStop,
                onOpenPane,
            }}
        />
    );
};

// A per-message copy affordance. The clipboard write is IMPURE and lives in the
// click handler (the sanctioned boundary — an event handler is not pure render);
// `navigator.clipboard` is a browser API the shell owns. View-local `copied`
// flips the label + glyph to a brief confirmation, auto-reset by a timeout
// (effect with cleanup). A rejected write silently stays un-copied — no false
// "Copied". The button is always in the tab order; CSS reveals it on
// hover/focus-within, so keyboard users get it via focus.
const CopyButton = ({ text }: { readonly text: string }): ReactNode => {
    const { t } = useLingui();
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!copied) return undefined;
        const id = setTimeout(() => setCopied(false), 1500);
        return () => clearTimeout(id);
    }, [copied]);
    const onCopy = () => {
        navigator.clipboard.writeText(text).then(
            () => setCopied(true),
            () => setCopied(false),
        );
    };
    return (
        <IconButton
            label={copied ? t`Copied` : t`Copy message`}
            variant="ghost"
            size="sm"
            className="bilda-chat-message__copy"
            onClick={onCopy}
        >
            <Icon name={copied ? "check" : "copy"} />
        </IconButton>
    );
};

// The meta row under an author/agent message: the always-visible relative
// timestamp (when the entry carries one) plus the hover-revealed copy button.
const MessageFooter = ({
    timestamp,
    copyText,
}: {
    readonly timestamp?: number;
    readonly copyText: string;
}): ReactNode => (
    <div className="bilda-chat-message__meta">
        {timestamp !== undefined ? (
            <MessageTimestamp timestamp={timestamp} />
        ) : null}
        <CopyButton text={copyText} />
    </div>
);

export type ChatMessageProps = {
    readonly entry: ChatEntry;
    // The reading contract for this entry — roomy (`full`) or dense (`compact`).
    readonly density?: ChatDensity;
    // Proposal pointer intents — meaningful only for the proposal kind.
    readonly onGoToChange?: (id: string) => void;
    readonly onReviewInPlace?: (id: string) => void;
    // A waiting proposal's Accept / Discard (by the proposal's id).
    readonly onAcceptProposal?: (proposalId: string) => void;
    readonly onDiscardProposal?: (proposalId: string) => void;
    // Open a created material as a focused tab — meaningful only for the
    // material kind. When absent the card's Open action is not offered (no
    // dead button), same posture as the proposal chip's onGoToChange.
    readonly onOpenMaterial?: (materialId: string) => void;
    // Live-build intents — meaningful only for the material_building kind.
    // Absent ⇒ the card omits that affordance (no dead button); the composer's
    // own Stop remains the turn's cancel until the host wires these.
    readonly onStopBuild?: (entryId: string) => void;
    readonly onOpenBuildPane?: (entryId: string) => void;
    readonly onRetryBuild?: (entryId: string) => void;
    // Open/download an exported package — meaningful only for the export kind.
    // Same no-dead-button posture as onOpenMaterial.
    readonly onOpenExport?: (exportId: string) => void;
    readonly onDownloadExport?: (exportId: string) => void;
    // Turn-epilogue intents (LANE S3). `onSubmitForm` / `onDismissForm` are
    // meaningful only for the information_request kind (the durable form
    // round-trip); `onSendPrompt` re-drives the agent for an action_offer's
    // `send_prompt` move. All optional — absent ⇒ that affordance is omitted (no
    // dead button), same posture as onOpenMaterial.
    readonly onSubmitForm?: (
        requestId: string,
        answers: readonly ChatFieldAnswer[],
    ) => void;
    readonly onDismissForm?: (requestId: string) => void;
    // The library's sources a `field_sources` form question offers (D42).
    readonly formSources?: readonly FormSourceOption[];
    readonly onSendPrompt?: (prompt: string) => void;
    // Open the shell's file picker for one Source (F9/F15's `request_upload`
    // move) — same no-dead-button posture as onOpenMaterial.
    readonly onRequestUpload?: (
        sourceId: string,
        sourceTitle: string | undefined,
    ) => void;
    // Open a cited source (cite-B deep-link) — meaningful only for an agent
    // message that carries citations. Absent ⇒ the citation UI is omitted (same
    // no-dead-button posture as onOpenMaterial): its markers open nothing, so it
    // renders only where the host wires the open-resource path.
    readonly onOpenSource?: (resourceId: string) => void;
    // Open a resource a tool call named (F124) in the right panel. Absent ⇒
    // the tool row names it as plain text (no dead link).
    readonly onOpenResource?: (ref: ResourceRef, target?: FocusTarget) => void;
    // Parse a link in the agent's markdown (`[title](/library/{id})`) into a
    // resource / external / plain target. Absent ⇒ every link is external.
    readonly resolveLink?: ResolveInlineLink;
    // Whether a turn is in flight (D51) — meaningful only for the
    // agent_activity kind, whose still-running calls then belong to the live
    // row instead of the trail. The caller (ChatTranscript) decides it from
    // the turn's drafting status. Absent/false ⇒ every step shows.
    readonly inFlight?: boolean;
    // Rendered inside an opened trail line: tool activity shows its steps
    // directly (meaningful only for the agent_activity kind).
    readonly inTrailRun?: boolean;
    // The interruption notice's recovery — meaningful only for the system
    // kind. Absent ⇒ the notice offers no action (ChatTranscript wires it only
    // on the live tail, so scrollback never carries a stale button).
    readonly onInterruptionAction?: (action: InterruptionAction) => void;
    // Where a notice (system kind) sits in its turn; see NoticeContext.
    readonly noticeContext?: NoticeContext;
    // Your message the server never got (author kind): "Not sent. Try again".
    readonly unsent?: UnsentState;
    // The assistant's questions are answered in the composer's place (pen
    // "Chat – every entry" §4): in the thread a settled question is one
    // trail line. `onAnswerNow` marks a question you skipped.
    readonly questionsInComposer?: boolean;
    readonly onAnswerNow?: () => void;
    readonly className?: string;
};

// The writing dots under a reply that is still growing (pen "Skriver").
const WritingDots = (): ReactNode => (
    <span className="bilda-chat-message__writing" aria-hidden="true">
        <span />
        <span />
        <span />
    </span>
);

// A material's kind and size, in words ("Course · 12 questions").
const materialDetail = (
    kind: MaterialKind,
    itemCount: number | undefined,
): MessageDescriptor => {
    switch (kind) {
        case "course":
            return itemCount === undefined
                ? msg`Course`
                : msg`Course · ${plural(itemCount, { one: "# question", other: "# questions" })}`;
        case "freeform":
            return msg`Notes`;
        default:
            return exhaustSwitchCase(kind);
    }
};

// One op count in words, for a settled proposal's line ("1 changed").
const opPhrase = (part: ProposalOpCount): MessageDescriptor => {
    switch (part.type) {
        case "added":
            return msg`${part.count} added`;
        case "edited":
            return msg`${part.count} changed`;
        case "removed":
            return msg`${part.count} removed`;
        case "moved":
            return msg`${part.count} moved`;
        default:
            return exhaustSwitchCase(part);
    }
};

// A settled proposal as one trail line (pen "Accepted · 2 added, 1 changed",
// "Discarded · Quiz: 3 added"); undefined while it waits for you.
const proposalSettledLine = (
    entry: ProposalEntry,
    i18n: I18n,
): { readonly glyph: TrailGlyph; readonly text: string } | undefined => {
    const ops = entry.summary.map((part) => i18n._(opPhrase(part))).join(", ");
    const what =
        entry.materialTitle === undefined
            ? ops
            : i18n._(msg`${entry.materialTitle}: ${ops}`);
    switch (entry.status.type) {
        case "pending":
            return undefined;
        case "accepted":
            return { glyph: "done", text: i18n._(msg`Accepted · ${what}`) };
        case "rejected":
            return {
                glyph: "discarded",
                text: i18n._(msg`Discarded · ${what}`),
            };
        default:
            return exhaustSwitchCase(entry.status);
    }
};

// The agent's answer at an AMBIENT density (compact), CLAMPED to a few lines so
// long content never floods the transcript (REQ-UI-004 / "summarize, don't
// flood"). The clamp count is a layout contract, not a theme token. A
// "more"/"less" toggle appears ONLY when the text actually overflows the clamp —
// measured post-layout, the same view-local geometry the transcript uses for
// tail-pinning. The FULL text stays in the DOM (line-clamp is visual), so
// assistive tech reads the whole answer; the toggle is the sighted reader's
// expand and announces its state via aria-expanded. Overflow is measured only
// WHILE clamped (expanding removes the clamp, so the last clamped verdict is
// retained — the "Less" control persists). Markdown rendering is Prose's job
// (react-markdown → React elements, never innerHTML — XSS-safe by construction),
// so the agent's bold/lists/headings render FORMATTED, not raw. The `full`
// density never mounts this — the roomy answer renders as a plain Prose (see the
// carve-out in the header note).
const ClampedProse = ({
    markdown,
    renderLink,
}: {
    readonly markdown: string;
    readonly renderLink?: RenderProseLink;
}): ReactNode => {
    const { t } = useLingui();
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `expanded` gates the measure (skip while open, so the clamp isn't removed under the ruler) and re-runs it on collapse; markdown is the content signal.
    useLayoutEffect(() => {
        if (expanded) return; // measure only in the clamped state
        const el = bodyRef.current;
        if (el === null) return;
        setOverflowing(el.scrollHeight - el.clientHeight > 1);
    }, [markdown, expanded]);
    return (
        <div className="bilda-chat-message__agent-clamped">
            <div
                ref={bodyRef}
                className={`bilda-chat-message__clamp${
                    expanded ? " bilda-chat-message__clamp--open" : ""
                }`}
            >
                <Prose renderLink={renderLink}>{markdown}</Prose>
            </div>
            {overflowing ? (
                <button
                    type="button"
                    className="bilda-chat-message__more"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((open) => !open)}
                >
                    {expanded ? t`Less` : t`More`}
                </button>
            ) : null}
        </div>
    );
};

// A sent @-mention as the resource it names (the composer says "package" for
// the domain's "export"). Total switch, no cast.
const refOfMention = (m: ComposerResourceRef): ResourceRef => {
    const { id, title } = m;
    switch (m.kind) {
        case "source":
            return { type: "source", id, title };
        case "material":
            return { type: "material", id, title };
        case "package":
            return { type: "export", id, title };
        case "file":
            return { type: "file", id, title };
        default:
            return exhaustSwitchCase(m.kind);
    }
};

// The agent's links: a resource route becomes the @-mention chip that opens the
// resource in the right panel; see InlineLink. Absent resolver ⇒ Prose default.
const agentLinkRenderer = (
    resolveLink: ResolveInlineLink | undefined,
    onOpenResource: ((ref: ResourceRef) => void) | undefined,
): RenderProseLink | undefined =>
    resolveLink === undefined
        ? undefined
        : ({ href, children }) => (
              <InlineLink
                  view={selectInlineLinkView(
                      href === undefined
                          ? { type: "plain", reason: "unsupported_href" }
                          : resolveLink(href),
                      onOpenResource,
                  )}
              >
                  {children}
              </InlineLink>
          );

// The Author's words (D8): plain text, never markdown, except an @-mention
// token `[title](/library/{id})`, which renders at its position as the same
// chip an agent link gets. A resource the library no longer knows (or no
// resolver wired) degrades to the title as written: no broken chip.
const AuthorText = ({
    text,
    resolveLink,
    onOpenResource,
}: {
    readonly text: string;
    readonly resolveLink: ResolveInlineLink | undefined;
    readonly onOpenResource: ((ref: ResourceRef) => void) | undefined;
}): ReactNode =>
    Core.ProseLink.mentionParts(text).map((part, i) => {
        const key = `${i}`;
        switch (part.type) {
            case "text_part":
                return <span key={key}>{part.text}</span>;
            case "mention_part":
                return (
                    <InlineLink
                        key={key}
                        view={selectInlineLinkView(
                            resolveLink === undefined
                                ? { type: "plain", reason: "unsupported_href" }
                                : resolveLink(
                                      Core.ProseLink.toHref(
                                          Core.ProseLink.mkResourceLink(
                                              part.resourceId,
                                          ),
                                      ),
                                  ),
                            onOpenResource,
                        )}
                    >
                        {part.title}
                    </InlineLink>
                );
            default:
                return exhaustSwitchCase(part);
        }
    });

export const ChatMessage = ({
    entry,
    density = "full",
    onGoToChange,
    onReviewInPlace,
    onAcceptProposal,
    onDiscardProposal,
    onOpenMaterial,
    onStopBuild,
    onOpenBuildPane,
    onRetryBuild,
    onOpenExport,
    onDownloadExport,
    onSubmitForm,
    onDismissForm,
    formSources,
    onSendPrompt,
    onRequestUpload,
    onOpenSource,
    onOpenResource,
    resolveLink,
    inFlight,
    inTrailRun = false,
    onInterruptionAction,
    noticeContext = { turnMadeSomething: false, live: false },
    unsent,
    questionsInComposer = false,
    onAnswerNow,
    className,
}: ChatMessageProps) => {
    const { t, i18n } = useLingui();
    const wrap = (kind: string, body: ReactNode) => (
        <div
            data-entry-id={entry.id}
            className={`bilda-chat-message bilda-chat-message--${kind} bilda-chat-message--${density}${
                className ? ` ${className}` : ""
            }`}
        >
            {body}
        </div>
    );

    const compact = density === "compact";

    switch (entry.type) {
        case "author": {
            // D4: the position-less REFERENCE chips (attached files; a legacy
            // message's @-mentions) — static atoms naming resources this
            // message pointed the agent at (north-star O3). Inline mentions
            // render in the text itself (D8).
            // The sent bubble is the reference's durable home; it never renders
            // in the Sources tier for a non-source kind. Absent/empty ⇒ no row.
            const authorText = (
                <AuthorText
                    text={entry.text}
                    resolveLink={resolveLink}
                    onOpenResource={onOpenResource}
                />
            );
            const mentions = entry.mentions ?? [];
            const mentionChips =
                mentions.length > 0 ? (
                    <MessageAttachmentIndicator
                        attachments={mentions}
                        attachmentRole="attached"
                        density={density}
                        onOpen={
                            onOpenResource === undefined
                                ? undefined
                                : (m) => onOpenResource(refOfMention(m))
                        }
                    />
                ) : null;
            // Compact: one dim line — "You  <text>", no bubble chrome. The
            // human's words are context for the answer, not a co-equal artifact
            // in a 360px box (chat-ux.md §3 rule 2). The visible "You" tag is
            // aria-hidden — the VisuallyHidden attribution already speaks it — and
            // the text truncates to one line while the full text rides `title`
            // and stays in the DOM for AT.
            if (compact) {
                return wrap(
                    "author",
                    <div
                        className="bilda-chat-message__author-line"
                        title={Core.ProseLink.mentionsAsTitles(entry.text)}
                    >
                        <VisuallyHidden>
                            <Trans>You said:</Trans>{" "}
                        </VisuallyHidden>
                        <span
                            className="bilda-chat-message__author-tag"
                            aria-hidden="true"
                        >
                            <Trans>You</Trans>
                        </span>
                        <span className="bilda-chat-message__author-text">
                            {authorText}
                        </span>
                        {mentionChips}
                    </div>,
                );
            }
            // Full: the bubble (folds after 6 lines), the chips of what it
            // pointed at, and "Not sent" when the server never got it. The
            // copy affordance stays hover-revealed and takes no room.
            return wrap(
                "author",
                <div className="bilda-chat-message__author-col">
                    <AuthorBubble
                        text={entry.text}
                        body={authorText}
                        chips={mentionChips}
                        unsent={unsent}
                    />
                    <MessageFooter
                        timestamp={entry.timestamp}
                        copyText={entry.text}
                    />
                </div>,
            );
        }
        case "agent": {
            // Källhänvisningar (pen "Chat – every entry" §2): markers in the
            // text, "N sources" under it. Only where the host can open a
            // source (no dead marker).
            const citations = entry.citations ?? [];
            const openCitation =
                onOpenResource ??
                (onOpenSource === undefined
                    ? undefined
                    : (ref: ResourceRef) => onOpenSource(ref.id));
            const cited = citations.length > 0 && openCitation !== undefined;
            // Full density (the roomy reading tab): the answer is the primary
            // artifact the author came to read — render it UNCLIPPED, no clamp,
            // no expander (owner carve-out 2026-08-31; see header note). Compact
            // (the ambient quick-ask surface) keeps summary-first: clamp + "more".
            return wrap(
                "agent",
                <div className="bilda-chat-message__agent">
                    <div
                        className={`bilda-chat-message__agent-body${cited ? " bilda-chat-message__agent-body--cited" : ""}`}
                    >
                        {/* Speaker attribution for AT — the log reads "Agent: …". */}
                        <VisuallyHidden>
                            <Trans>Agent said:</Trans>{" "}
                        </VisuallyHidden>
                        {compact ? (
                            <ClampedProse
                                markdown={entry.markdown}
                                renderLink={agentLinkRenderer(
                                    resolveLink,
                                    onOpenResource,
                                )}
                            />
                        ) : (
                            <StreamingProse
                                markdown={entry.markdown}
                                renderLink={agentLinkRenderer(
                                    resolveLink,
                                    onOpenResource,
                                )}
                                streaming={entry.streamSeq !== undefined}
                            />
                        )}
                        {cited ? (
                            <CitationMarkers
                                citations={citations}
                                onOpen={openCitation}
                            />
                        ) : null}
                        {entry.streamSeq !== undefined ? <WritingDots /> : null}
                    </div>
                    {cited ? (
                        <SourcesList
                            citations={citations}
                            onOpen={openCitation}
                        />
                    ) : null}
                    <MessageFooter
                        timestamp={entry.timestamp}
                        copyText={entry.markdown}
                    />
                </div>,
            );
        }
        case "proposal": {
            // Settled: one line in the trail (pen "Accepted" / "Discarded").
            const settled = proposalSettledLine(entry, i18n);
            if (settled !== undefined)
                return wrap(
                    "proposal",
                    <TrailLine glyph={settled.glyph} text={settled.text} />,
                );
            const { proposalId } = entry;
            // Compact: the tallest transcript atom collapses to a ONE-LINE chip
            // (chat-ux.md §3 rule 3) — "✎ 1 added · 1 edited → review". The full
            // ProposalCard lives in full/panel/Review; the chip POINTS there via
            // the same primary intent (onGoToChange). When no handler is wired the
            // chip is a static row, never a dead button (no decoy controls).
            if (compact) {
                const chipInner = (
                    <>
                        <Icon name="pencil" />
                        <Text
                            as="span"
                            size="sm"
                            weight="medium"
                            className="bilda-chat-message__proposal-summary"
                        >
                            <ProposalSummaryText summary={entry.summary} />
                        </Text>
                        {onGoToChange ? (
                            <span className="bilda-chat-message__proposal-review">
                                <Trans context="action">Review</Trans>
                                <Icon name="arrow-right" />
                            </span>
                        ) : null}
                    </>
                );
                return wrap(
                    "proposal",
                    onGoToChange ? (
                        <button
                            type="button"
                            className="bilda-chat-message__proposal-chip"
                            onClick={() => onGoToChange(entry.id)}
                        >
                            {chipInner}
                        </button>
                    ) : (
                        <div className="bilda-chat-message__proposal-chip bilda-chat-message__proposal-chip--static">
                            {chipInner}
                        </div>
                    ),
                );
            }
            return wrap(
                "proposal",
                <ProposalCard
                    summary={entry.summary}
                    rationale={entry.rationale}
                    grounding={entry.grounding}
                    materialTitle={entry.materialTitle}
                    onGoToChange={() => onGoToChange?.(entry.id)}
                    onReviewInPlace={
                        onReviewInPlace
                            ? () => onReviewInPlace(entry.id)
                            : undefined
                    }
                    onAccept={
                        proposalId === undefined ||
                        onAcceptProposal === undefined
                            ? undefined
                            : () => onAcceptProposal(proposalId)
                    }
                    onDiscard={
                        proposalId === undefined ||
                        onDiscardProposal === undefined
                            ? undefined
                            : () => onDiscardProposal(proposalId)
                    }
                />,
            );
        }
        case "plan":
            return wrap(
                "plan",
                <PlanPreview
                    steps={entry.steps}
                    onStepChange={() => {}}
                    onRemoveStep={() => {}}
                    onApprove={() => {}}
                />,
            );
        case "material":
            // A material the assistant made: one line, kind + size, Open.
            return wrap(
                "material",
                <ObjectLine
                    icon="module"
                    title={entry.title}
                    detail={i18n._(materialDetail(entry.kind, entry.itemCount))}
                    onOpen={
                        onOpenMaterial === undefined
                            ? undefined
                            : () => onOpenMaterial(entry.materialId)
                    }
                />,
            );
        case "material_building":
            // The LIVE half of the material card (material-streaming
            // 2026-09-01): the build streams a filling outline, then the durable
            // MaterialEntry supersedes it by id (see MaterialBuildingEntry). A
            // stopped build offers a re-run; otherwise it shows the honest
            // building status. Stop/pane/retry are wired only when the host
            // passes a handler (no dead button); until then the composer's own
            // Stop is the turn's cancel.
            return wrap(
                "material",
                entry.build.status === "stopped" ? (
                    <MaterialCard
                        state={{
                            type: "stopped",
                            build: entry.build,
                            onRetry: onRetryBuild
                                ? () => onRetryBuild(entry.id)
                                : undefined,
                        }}
                    />
                ) : (
                    <BuildingMaterialCard
                        entry={entry}
                        onStop={
                            onStopBuild
                                ? () => onStopBuild(entry.id)
                                : undefined
                        }
                        onOpenPane={
                            onOpenBuildPane
                                ? () => onOpenBuildPane(entry.id)
                                : undefined
                        }
                    />
                ),
            );
        case "export":
            // An export ready: one line; Open goes to the export (its file
            // and where it went).
            return wrap(
                "export",
                <ObjectLine
                    icon="export"
                    title={entry.title}
                    detail={t`Ready for your learning environment`}
                    onOpen={
                        onOpenExport === undefined
                            ? undefined
                            : () => onOpenExport(entry.exportId)
                    }
                />,
            );
        case "agent_activity":
            // The live status/tool-step trail — same treatment at every
            // density (it's already a one-line collapsed summary; compact
            // has nothing further to shrink).
            return wrap(
                "activity",
                <AgentActivityTrail
                    entry={entry}
                    inFlight={inFlight}
                    open={inTrailRun}
                    actions={{
                        openResource: onOpenResource,
                        downloadExport: onDownloadExport,
                        reuploadSource: onRequestUpload,
                    }}
                />,
            );
        case "system":
            // A closing-outcome notice — the same treatment at every density
            // and for every cause (D18): one notice, only the copy differs.
            return wrap(
                "system",
                <ThreadNotice
                    kind={entry.kind}
                    context={noticeContext}
                    onAction={onInterruptionAction}
                />,
            );
        case "information_request": {
            if (questionsInComposer && !compact) {
                if (entry.status.type === "answered")
                    return wrap(
                        "information-request",
                        <AnsweredQuestions answers={entry.answers ?? []} />,
                    );
                if (onAnswerNow !== undefined)
                    return wrap(
                        "information-request",
                        <SkippedQuestions onAnswerNow={onAnswerNow} />,
                    );
                // Pending: it waits in the composer's place, not here.
                if (entry.status.type === "pending") return null;
            }
            // The agent's structured question as an interactive inline card (the
            // durable ElicitationForm epilogue).
            const requestId = entry.id;
            return wrap(
                "information-request",
                <InformationRequestCard
                    entry={entry}
                    sources={formSources}
                    onSubmit={
                        onSubmitForm === undefined
                            ? undefined
                            : (answers) => onSubmitForm(requestId, answers)
                    }
                    onDismiss={
                        onDismissForm === undefined
                            ? undefined
                            : () => onDismissForm(requestId)
                    }
                />,
            );
        }
        case "information_request_pending":
            // F53: the agent is PREPARING a form — a DISTINCT form-load state (a
            // skeleton in the form's own visual language), NOT the generic working
            // indicator that reads as a freeze. Superseded by the real form the
            // instant it lands. Same at every density (already minimal).
            return wrap(
                "information-request-pending",
                <InformationRequestPendingCard parts={entry.parts} />,
            );
        case "information_submission":
            // A DANGLING answer resting read-only in scrollback — its
            // `information_request` could not be resolved, so there is no card to
            // fold into. It degrades to a quiet "answer submitted" line. A MATCHED
            // answer never reaches here: its read-back is nested on the request
            // card and renders inside the SAME card (F113).
            return wrap(
                "information-submission",
                <div className="bilda-chat-message__submission">
                    <VisuallyHidden>
                        <Trans>You answered:</Trans>{" "}
                    </VisuallyHidden>
                    <Icon name="check" />
                    <Text as="span" size="sm" tone="dim">
                        <Trans>Answer submitted</Trans>
                    </Text>
                </div>,
            );
        case "action_offer":
            // The transient turn-epilogue hand-off (A-17): concrete next moves
            // mapped to the existing entity handlers / a re-drive prompt. A move
            // with no wired handler is omitted (no dead button).
            return wrap(
                "action-offer",
                <ActionOffer
                    entry={entry}
                    onOpenMaterial={onOpenMaterial}
                    onOpenExport={onOpenExport}
                    onDownloadExport={onDownloadExport}
                    onSendPrompt={onSendPrompt}
                    onRequestUpload={onRequestUpload}
                />,
            );
        case "unknown":
            // The designed default: one generic line, never empty, never
            // raw data.
            return wrap(
                "unknown",
                <TrailLine
                    glyph="unknown"
                    text={t`An update this version cannot show yet`}
                />,
            );
        default: {
            // Exhaustiveness: a new ChatEntry variant is `never` here (no throw
            // — the value is assignable to the ReactNode return).
            const exhaustive: never = entry;
            return exhaustive;
        }
    }
};
